import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Col, Row, Table, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import Select from 'react-select';
import Swal from 'sweetalert2';

// Define the primary theme color
const themeColor = '#004aad';
const themeColorDarker = '#003a8c';

// API Base URL - Change this to your actual backend URL
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com'; // Changed from 5002 to 5002 based on your first code

const styles = {
  modalHeader: {
    backgroundColor: themeColor,
    color: '#fff',
    borderBottom: 'none',
  },
  modalTitle: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    color: themeColor,
    fontSize: '1.1rem',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #dee2e6',
  },
  requiredAsterisk: {
    color: 'red',
    marginLeft: '2px',
  },
  footerButton: {
    backgroundColor: themeColor,
    borderColor: themeColor,
    color: '#fff',
    minWidth: '80px',
  },
  footerButtonHover: {
    backgroundColor: themeColorDarker,
    borderColor: themeColorDarker,
  },
  datePickerInput: {
    display: 'block',
    width: '100%',
    padding: '.375rem .75rem',
    fontSize: '1rem',
    fontWeight: '400',
    lineHeight: '1.5',
    color: '#212529',
    backgroundColor: '#fff',
    backgroundClip: 'padding-box',
    border: '1px solid #ced4da',
    appearance: 'none',
    borderRadius: '.25rem',
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
  },
  tableContainer: {
    maxHeight: '350px',
    overflowY: 'auto',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
  },
};

function Payment({ show, handleClose }) {

  // User identification
  const [userLabId, setUserLabId] = useState('');
  const [backendError, setBackendError] = useState(false);

  const [paymentDate, setPaymentDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [memo, setMemo] = useState('');
  const [amountToApply, setAmountToApply] = useState(0);
  const [unappliedCredit, setUnappliedCredit] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentMethods] = useState([
    { id: 'cash', name: 'Cash' },
    { id: 'cc', name: 'Credit Card' },
    { id: 'bank', name: 'Bank Transfer' },
    { id: 'cheque', name: 'Cheque' },
  ]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastStatementOnly, setLastStatementOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [lastRunDateStatements, setLastRunDateStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);

  // Load userLabId from localStorage when modal opens
  useEffect(() => {
    if (show) {
      const storedUserLabId = localStorage.getItem('userLabId');
      if (storedUserLabId) {
        const cleanedId = String(storedUserLabId).trim();
        setUserLabId(cleanedId);
        setBackendError(false);
      } else {
        setError('User identification not found. Please log in again.');
      }
    }
  }, [show]);

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      border: '1px solid #ced4da',
      borderRadius: '.25rem',
      minHeight: 'calc(1.5em + .75rem + 2px)',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #ced4da',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '.25rem',
      border: '1px solid #ced4da',
      boxShadow: '0 0.5rem 1rem rgba(0,0,0,.15)',
    }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: state.isSelected ? themeColor : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: state.isSelected ? themeColor : '#f8f9fa',
        color: state.isSelected ? 'white' : 'black',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#212529',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  // Fetch accounts for the current lab
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        setError('');

        if (!userLabId) {
          setLoadingAccounts(false);
          return;
        }

        console.log('Fetching accounts for lab:', userLabId);

        const response = await fetch(`${API_BASE_URL}/api/account/lab-accounts/${userLabId}`);

        if (!response.ok) {
          if (response.status === 404) {
            setAccounts([]);
            setError('No accounts found for this lab.');
          } else if (response.status === 0 || response.status === -1) {
            throw new Error('Cannot connect to server. Please check if the backend is running.');
          } else {
            throw new Error(`Failed to fetch accounts: ${response.status}`);
          }
        } else {
          const result = await response.json();
          if (result.success && Array.isArray(result.data)) {
            // Sort accounts alphabetically by account_name
            const sortedAccounts = [...result.data].sort((a, b) => {
              const nameA = a.account_name.toLowerCase();
              const nameB = b.account_name.toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            });
            setAccounts(sortedAccounts);
            setBackendError(false);
          } else if (result.success && result.message === 'No accounts found for this lab.') {
            setAccounts([]);
          } else {
            throw new Error('Invalid data format received from backend');
          }
        }
      } catch (fetchError) {
        console.error('Error fetching accounts:', fetchError);
        setBackendError(true);
        setError('Failed to connect to server. Please ensure the backend server is running on port 5002.');
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (show && userLabId) {
      fetchAccounts();
    } else if (show && !userLabId) {
      setLoadingAccounts(false);
      setAccounts([]);
    }
  }, [show, userLabId]);

  // Fetch last run date statements
  useEffect(() => {
    const fetchLastStatements = async () => {
      if (!show) return;

      try {
        setLoadingStatements(true);
        console.log('Fetching last statements...');

        const response = await fetch(`${API_BASE_URL}/api/statements/last-run-date`);

        if (!response.ok) {
          if (response.status === 0 || response.status === -1) {
            throw new Error('Cannot connect to server');
          }
          throw new Error(`Failed to fetch last statements: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data?.statements) {
          setLastRunDateStatements(result.data.statements);
        } else {
          setLastRunDateStatements([]);
        }
      } catch (error) {
        console.error('Error fetching last statements:', error);
        // Don't set backend error here as this is a secondary feature
        setLastRunDateStatements([]);
      } finally {
        setLoadingStatements(false);
      }
    };

    if (show) {
      fetchLastStatements();
    }
  }, [show]);

  // Update selectedAccountId and accountName when selectedAccount changes
  useEffect(() => {
    if (selectedAccount) {
      const account = accounts.find(acc => acc._id === selectedAccount);
      setSelectedAccountId(account ? account.accountId : '');
      setAccountName(account ? account.account_name : '');
    } else {
      setSelectedAccountId('');
      setAccountName('');
      setSelectedInvoices([]);
      setReferenceNo('');
      setAmount('');
      setAmountToApply(0);
      setUnappliedCredit(0);
      setTableData([]);
    }
  }, [selectedAccount, accounts]);

  // Helper function to parse invoice number for sorting
  const parseInvoiceNumber = (invoiceNumber) => {
    if (!invoiceNumber) return { prefix: '', number: 0 };

    const match = invoiceNumber.toString().match(/(\D*)(\d+)/);
    if (match) {
      return {
        prefix: match[1] || '',
        number: parseInt(match[2], 10) || 0
      };
    }

    return { prefix: invoiceNumber.toString(), number: 0 };
  };

  // Fetch invoices when selectedAccountId changes
  useEffect(() => {
    const fetchInvoices = async (accountId) => {
      if (!accountId) {
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAmountToApply(0);
        setUnappliedCredit(0);
        return;
      }

      try {
        setError('');
        console.log('Fetching invoices for account:', accountId);

        const response = await fetch(`${API_BASE_URL}/api/invoices/invoices/account?accountId=${accountId}`);

        if (!response.ok) {
          if (response.status === 0 || response.status === -1) {
            throw new Error('Cannot connect to server');
          }
          throw new Error(`Failed to fetch invoices: ${response.status}`);
        }

        const invoices = await response.json();

        // Map invoices
        const formattedInvoices = invoices.map(invoice => {
          const parsedInvoiceNumber = parseInvoiceNumber(invoice.invoiceNumber);

          return {
            id: invoice._id || invoice.invoiceNumber,
            invoiceNumber: invoice.invoiceNumber,
            description: `Invoice ${invoice.invoiceNumber}`,
            invoiceDate: format(new Date(invoice.invoiceDate), 'yyyy-MM-dd'),
            invoiceDateObj: new Date(invoice.invoiceDate),
            total: invoice.total || 0,
            paid: invoice.paid || 0,
            originalRemaining: Math.max(0, (invoice.total || 0) - (invoice.paid || 0)),
            remaining: Math.max(0, (invoice.total || 0) - (invoice.paid || 0)),
            payment: 0,
            parsedInvoiceNumber: parsedInvoiceNumber,
          };
        });

        // Sort invoices
        const sortedInvoices = [...formattedInvoices].sort((a, b) => {
          const aParsed = a.parsedInvoiceNumber;
          const bParsed = b.parsedInvoiceNumber;

          if (aParsed.prefix < bParsed.prefix) return -1;
          if (aParsed.prefix > bParsed.prefix) return 1;
          return aParsed.number - bParsed.number;
        });

        // Apply filters
        let filteredInvoices = sortedInvoices;
        if (showFilters) {
          // Date filters
          if (startDate || endDate) {
            filteredInvoices = filteredInvoices.filter(inv => {
              const invDate = new Date(inv.invoiceDate + 'T00:00:00');
              const start = startDate ? new Date(startDate) : null;
              const end = endDate ? new Date(endDate) : null;
              if (start) start.setHours(0, 0, 0, 0);
              if (end) end.setHours(23, 59, 59, 999);
              if (start && invDate < start) return false;
              if (end && invDate > end) return false;
              return true;
            });
          }

          // Last statement filter
          if (lastStatementOnly && lastRunDateStatements.length > 0) {
            const accountStatements = lastRunDateStatements.filter(
              statement => statement.accountId === accountId
            );

            if (accountStatements.length > 0) {
              const lastStatementInvoiceNumbers = new Set();
              accountStatements.forEach(statement => {
                if (statement.invoices && Array.isArray(statement.invoices)) {
                  statement.invoices.forEach(invoice => {
                    if (invoice.invoiceNumber) {
                      lastStatementInvoiceNumbers.add(invoice.invoiceNumber);
                    }
                  });
                }
              });

              filteredInvoices = filteredInvoices.filter(invoice =>
                lastStatementInvoiceNumbers.has(invoice.invoiceNumber)
              );
            } else {
              filteredInvoices = [];
            }
          }
        }

        // Only show invoices with remaining balance
        filteredInvoices = filteredInvoices.filter(inv => inv.remaining > 0);

        // Remove parsedInvoiceNumber property
        const finalInvoices = filteredInvoices.map(({ parsedInvoiceNumber, ...rest }) => rest);

        setTableData(finalInvoices);

        // Update selected invoices to only include those that still exist
        const updatedSelectedInvoices = selectedInvoices.filter(id =>
          finalInvoices.some(row => row.id === id)
        );
        setSelectedInvoices(updatedSelectedInvoices);

      } catch (invoiceError) {
        console.error('Error fetching invoices:', invoiceError);
        setError('Error fetching invoices: ' + invoiceError.message);
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAmountToApply(0);
        setUnappliedCredit(0);
      }
    };

    fetchInvoices(selectedAccountId);
  }, [selectedAccountId, startDate, endDate, showFilters, lastStatementOnly, lastRunDateStatements]);

  // Update referenceNo when selectedInvoices change
  useEffect(() => {
    const selectedRows = tableData.filter(row => selectedInvoices.includes(row.id));
    const invoiceNumbers = selectedRows.map(row => row.invoiceNumber);
    setReferenceNo(invoiceNumbers.join(', '));
  }, [selectedInvoices, tableData]);

  // Update amountToApply and unappliedCredit
  useEffect(() => {
    const totalApplied = tableData.reduce((sum, row) => sum + parseFloat(row.payment || 0), 0);
    setAmountToApply(totalApplied);

    const numericAmount = parseFloat(amount || 0);
    if (!isNaN(numericAmount)) {
      const unapplied = numericAmount - totalApplied;
      setUnappliedCredit(unapplied > 0 ? unapplied : 0);
    } else {
      setUnappliedCredit(0);
    }
  }, [amount, tableData]);

  // Calculate total payment when invoices are selected/deselected
  useEffect(() => {
    const calculateTotalFromSelectedInvoices = () => {
      const totalPayment = selectedInvoices.reduce((sum, invoiceId) => {
        const invoice = tableData.find(row => row.id === invoiceId);
        return sum + (invoice ? parseFloat(invoice.payment || 0) : 0);
      }, 0);

      return totalPayment;
    };

    if (selectedInvoices.length > 0) {
      const totalPayment = calculateTotalFromSelectedInvoices();
      setAmount(totalPayment > 0 ? totalPayment.toString() : '');
    } else {
      if (selectedInvoices.length === 0 && !selectedAccountId) {
        setAmount('');
      }
    }
  }, [selectedInvoices, tableData]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'account') {
      setSelectedAccount(value);
    } else if (name === 'amount') {
      setAmount(value);

      // Auto-select invoices based on entered amount
      if (value && !isNaN(parseFloat(value)) && selectedAccountId) {
        const enteredAmount = parseFloat(value);
        let remainingAmount = enteredAmount;
        const newSelectedInvoices = [];

        const updatedTableData = tableData.map(row => {
          return {
            ...row,
            payment: 0,
            remaining: row.originalRemaining
          };
        });

        const sortedInvoices = [...updatedTableData].sort((a, b) => {
          const dateA = new Date(a.invoiceDate);
          const dateB = new Date(b.invoiceDate);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return a.invoiceNumber.localeCompare(b.invoiceNumber);
        });

        const finalTableData = sortedInvoices.map(row => {
          if (remainingAmount <= 0) return row;

          if (row.originalRemaining > 0) {
            if (remainingAmount >= row.originalRemaining) {
              newSelectedInvoices.push(row.id);
              remainingAmount -= row.originalRemaining;
              return {
                ...row,
                payment: row.originalRemaining,
                remaining: 0
              };
            } else {
              newSelectedInvoices.push(row.id);
              const paymentAmount = remainingAmount;
              remainingAmount = 0;
              return {
                ...row,
                payment: paymentAmount,
                remaining: row.originalRemaining - paymentAmount
              };
            }
          }
          return row;
        });

        setSelectedInvoices(newSelectedInvoices);
        setTableData(finalTableData);

      } else if (!value) {
        setSelectedInvoices([]);
        const resetTableData = tableData.map(row => ({
          ...row,
          payment: 0,
          remaining: row.originalRemaining
        }));
        setTableData(resetTableData);
      }
    } else if (name === 'referenceNo') {
      setReferenceNo(value);
    } else if (name === 'memo') {
      setMemo(value);
    } else if (name === 'paymentMethod') {
      setPaymentMethod(value);
    } else if (name === 'lastStatementOnly') {
      setLastStatementOnly(checked);
    }
  };

  const handleInvoiceSelection = (invoiceId) => {
    const isSelected = selectedInvoices.includes(invoiceId);

    if (isSelected) {
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));

      const updatedTableData = tableData.map(row => {
        if (row.id === invoiceId) {
          return {
            ...row,
            payment: 0,
            remaining: row.originalRemaining,
          };
        }
        return row;
      });

      setTableData(updatedTableData);
    } else {
      setSelectedInvoices(prev => [...prev, invoiceId]);

      const updatedTableData = tableData.map(row => {
        if (row.id === invoiceId) {
          return {
            ...row,
            payment: row.originalRemaining,
            remaining: 0,
          };
        }
        return row;
      });

      setTableData(updatedTableData);
    }
  };

  const handleInvoicePaymentChange = (index, paymentValue) => {
    const updatedTableData = [...tableData];
    const row = updatedTableData[index];
    const newPayment = parseFloat(paymentValue || 0);

    if (row) {
      const maxAllowedPayment = row.originalRemaining;
      const appliedPayment = Math.min(newPayment, maxAllowedPayment);
      updatedTableData[index] = {
        ...row,
        payment: appliedPayment,
        remaining: row.originalRemaining - appliedPayment,
      };
      setTableData(updatedTableData);
    }
  };

  const handleSelectAll = () => {
    if (tableData.length === 0) return;

    const allInvoiceIds = tableData.map(row => row.id);
    setSelectedInvoices(allInvoiceIds);

    const updatedTableData = tableData.map(row => ({
      ...row,
      payment: row.originalRemaining,
      remaining: 0,
    }));

    setTableData(updatedTableData);
  };

  const handleClearAll = () => {
    setSelectedInvoices([]);

    const updatedTableData = tableData.map(row => ({
      ...row,
      payment: 0,
      remaining: row.originalRemaining,
    }));

    setTableData(updatedTableData);
    setAmount('');
  };

  const handleFilterToggle = (e) => {
    const isChecked = e.target.checked;
    setShowFilters(isChecked);

    if (!isChecked) {
      setStartDate(null);
      setEndDate(null);
      setLastStatementOnly(false);
    }
  };

  const handleSave = async () => {
    if (!userLabId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to process payment: User identification not found. Please log in again.',
        icon: 'error',
        confirmButtonColor: themeColor
      });
      return;
    }

    if (!selectedAccount || !paymentDate || !paymentMethod || !amount || !selectedInvoices.length) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields (*) and select at least one invoice.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    const numericAmount = parseFloat(amount || 0);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid positive amount.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    const appliedInvoices = tableData
      .filter(row => selectedInvoices.includes(row.id) && parseFloat(row.payment || 0) > 0)
      .map(row => {
        const existingPaid = parseFloat(row.paid || 0);
        const newPayment = parseFloat(row.payment || 0);
        const newPaidAmount = existingPaid + newPayment;

        return {
          invoiceNumber: row.invoiceNumber,
          appliedAmount: newPayment,
          newPaidAmount: newPaidAmount
        };
      });

    if (appliedInvoices.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter payment amounts for selected invoices.',
        icon: 'warning',
        confirmButtonColor: themeColor
      });
      return;
    }

    const referenceNoArray = selectedInvoices
      .map(id => tableData.find(row => row.id === id)?.invoiceNumber)
      .filter(Boolean);

    const paymentData = {
      accountId: selectedAccountId,
      accountName,
      amountReceived: numericAmount,
      paymentDate: format(paymentDate, 'yyyy-MM-dd'),
      paymentMethod,
      referenceNo: referenceNoArray,
      memo,
      appliedInvoices,
      finalAppliedAmount: amountToApply,
      finalUnappliedAmount: unappliedCredit,
      addedBy: userLabId,
      uniquePaymentId: `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    try {
      const paymentResponse = await fetch(`${API_BASE_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(paymentData),
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save payment: ${paymentResponse.status}`);
      }

      const responseData = await paymentResponse.json();

      if (responseData.success) {
        Swal.fire({
          title: 'Success!',
          text: responseData.message || 'Payment saved successfully!',
          icon: 'success',
          confirmButtonColor: themeColor
        });

        handleCloseModal();
      } else {
        throw new Error(responseData.message || 'Failed to save payment');
      }

    } catch (error) {
      console.error('Error saving payment:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to save payment: ' + error.message,
        icon: 'error',
        confirmButtonColor: themeColor
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedAccount('');
    setSelectedAccountId('');
    setAccountName('');
    setPaymentDate(new Date());
    setAmount('');
    setReferenceNo('');
    setMemo('');
    setTableData([]);
    setSelectedInvoices([]);
    setAmountToApply(0);
    setUnappliedCredit(0);
    setError('');
    setLoadingAccounts(true);
    setPaymentMethod('');
    setStartDate(null);
    setEndDate(null);
    setLastStatementOnly(false);
    setShowFilters(true);
    setLastRunDateStatements([]);
    setLoadingStatements(false);
    setBackendError(false);
    handleClose();
  };

  const CustomDateInput = React.forwardRef(({ value, onClick, name }, ref) => (
    <Form.Control
      type="text"
      value={value}
      onClick={onClick}
      ref={ref}
      readOnly
      name={name}
      style={styles.datePickerInput}
    />
  ));

  const renderStatementFilterInfo = () => {
    if (loadingStatements) {
      return <small className="text-muted ms-2">Loading statements...</small>;
    }
    return null;
  };

  const allSelected = tableData.length > 0 && selectedInvoices.length === tableData.length;

  return (
    <Modal show={show} onHide={handleCloseModal} backdrop="static" keyboard={false} size="lg" className="create-payment-modal">
      <Modal.Header closeButton style={styles.modalHeader}>
        <Modal.Title style={styles.modalTitle}>Create New Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '1.5rem', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {error && <div className="alert alert-danger">{error}</div>}

        {backendError && (
          <div className="alert alert-warning">
            <strong>Connection Issue:</strong> Unable to connect to the server. Please ensure the backend server is running on port 5002.
          </div>
        )}

        {!userLabId && show && (
          <div className="alert alert-warning">
            <strong>Warning:</strong> User not identified. Please log in again.
          </div>
        )}

        <Form noValidate onSubmit={(e) => e.preventDefault()}>
          <h4 style={styles.sectionTitle}>Payment Details</h4>
          <Row className="mb-3">
            <Col md={5}>
              <Form.Group controlId="receivePaymentAccount">
                <Form.Label>Account<span style={styles.requiredAsterisk}>*</span></Form.Label>
                <Select
                  options={accounts.map((account) => ({ value: account._id, label: account.account_name }))}
                  value={selectedAccount ? { value: selectedAccount, label: accountName } : null}
                  onChange={(selectedOption) => handleInputChange({ target: { name: 'account', value: selectedOption ? selectedOption.value : '' } })}
                  placeholder={loadingAccounts ? 'Loading accounts...' : !userLabId ? 'Please log in to view accounts' : accounts.length === 0 ? 'No accounts found for your lab' : 'Select Account'}
                  isDisabled={loadingAccounts || !userLabId || backendError}
                  isClearable={true}
                  styles={customStyles}
                  required
                />
                {userLabId && accounts.length === 0 && !loadingAccounts && !backendError && (
                  <Form.Text className="text-muted">
                    No accounts found for your lab. Please create an account first.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group controlId="receivePaymentAmount">
                <Form.Label>Amount<span style={styles.requiredAsterisk}>*</span></Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0.00"
                  name="amount"
                  value={amount}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                  disabled={!userLabId || backendError}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={5}>
              <Form.Group controlId="receivePaymentDate">
                <Form.Label>Payment Date<span style={styles.requiredAsterisk}>*</span></Form.Label>
                <InputGroup>
                  <DatePicker
                    selected={paymentDate}
                    onChange={(date) => setPaymentDate(date)}
                    dateFormat="d MMM yyyy"
                    customInput={<CustomDateInput name="paymentDate" />}
                    popperPlacement="bottom-start"
                    disabled={!userLabId || backendError}
                  />
                  <InputGroup.Text style={{ backgroundColor: 'white' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill={themeColor} className="bi bi-calendar-event" viewBox="0 0 16 16">
                      <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
                      <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                    </svg>
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group controlId="receivePaymentRefNo">
                <Form.Label>Reference No.</Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  name="referenceNo"
                  value={referenceNo}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={5}>
              <Form.Group controlId="receivePaymentMethod">
                <Form.Label>Payment Method<span style={styles.requiredAsterisk}>*</span></Form.Label>
                <Form.Select
                  name="paymentMethod"
                  value={paymentMethod}
                  onChange={handleInputChange}
                  required
                  disabled={!userLabId || backendError}
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>{method.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div style={styles.filterSection}>
            <h4 style={{ ...styles.sectionTitle, borderBottom: 'none', marginBottom: '0.5rem', display: 'inline-block' }}>
              Invoice Filters
            </h4>
            <Form.Check
              inline
              type="checkbox"
              id="filterToggleCheckbox"
              label="(hide & clear)"
              checked={showFilters}
              onChange={handleFilterToggle}
              style={{ marginLeft: '10px', fontSize: '0.9em' }}
              disabled={!userLabId || backendError}
            />
            {showFilters && (
              <div className="mt-3">
                <Row className="mb-3">
                  <Col md={5}>
                    <Form.Group controlId="filterStartDate">
                      <Form.Label>Start Date</Form.Label>
                      <InputGroup>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          dateFormat="d MMM yyyy"
                          customInput={<CustomDateInput name="startDate" />}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          isClearable
                          popperPlacement="bottom-start"
                          disabled={!userLabId || backendError}
                        />
                        <InputGroup.Text style={{ backgroundColor: 'white' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill={themeColor} className="bi bi-calendar-event" viewBox="0 0 16 16">
                            <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
                            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                          </svg>
                        </InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group controlId="filterEndDate">
                      <Form.Label>End Date</Form.Label>
                      <InputGroup>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          dateFormat="d MMM yyyy"
                          customInput={<CustomDateInput name="endDate" />}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          isClearable
                          popperPlacement="bottom-start"
                          disabled={!userLabId || backendError}
                        />
                        <InputGroup.Text style={{ backgroundColor: 'white' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill={themeColor} className="bi bi-calendar-event" viewBox="0 0 16 16">
                            <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
                            <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                          </svg>
                        </InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Check
                      type="checkbox"
                      id="lastStatementCheck"
                      label="From last statement only"
                      name="lastStatementOnly"
                      checked={lastStatementOnly}
                      onChange={handleInputChange}
                      disabled={loadingStatements || !userLabId || backendError}
                    />
                    {renderStatementFilterInfo()}
                  </Col>
                </Row>
              </div>
            )}
          </div>
          {selectedAccountId && userLabId && !backendError && (
            <>
              <h4 style={styles.sectionTitle} className="mt-4">Apply to Outstanding Invoices</h4>
              <div style={styles.tableContainer}>
                <Table striped bordered hover responsive className="mt-3 mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <Form.Check
                          type="checkbox"
                          checked={allSelected}
                          onChange={allSelected ? handleClearAll : handleSelectAll}
                          aria-label={allSelected ? "Deselect all invoices" : "Select all invoices"}
                          disabled={!userLabId || backendError}
                        />
                      </th>
                      <th>Description</th>
                      <th>Invoice Date</th>
                      <th className="text-end">Total</th>
                      <th className="text-end">Paid</th>
                      <th className="text-end">Remaining</th>
                      <th className="text-end" style={{ minWidth: '120px' }}>Payment</th>
                    </tr>
                  </thead>

                  <tbody>
                    {tableData.length > 0 ? tableData.map((row, index) => (
                      <tr key={row.id || index}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedInvoices.includes(row.id)}
                            onChange={() => handleInvoiceSelection(row.id)}
                            aria-label={`Select invoice ${row.invoiceNumber}`}
                            disabled={!userLabId || backendError}
                          />
                        </td>
                        <td>{row.description}</td>
                        <td>{format(new Date(row.invoiceDate + 'T00:00:00'), 'd MMM yyyy')}</td>
                        <td className="text-end">{(Number(row.total || 0))}</td>
                        <td className="text-end">{(Number(row.paid || 0))}</td>
                        <td className="text-end">{(Number(row.remaining || 0))}</td>
                        <td className="text-end">
                          <Form.Control
                            type="number"
                            size="sm"
                            className="text-end"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            max={Number(row.originalRemaining || 0)}
                            value={row.payment === 0 ? '' : row.payment}
                            onChange={(e) => handleInvoicePaymentChange(index, e.target.value)}
                            aria-label={`Payment for ${row.description}`}
                            disabled={!selectedInvoices.includes(row.id) || !userLabId || backendError}
                          />
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="text-center fst-italic text-muted py-3">
                          {lastStatementOnly && lastRunDateStatements.length === 0
                            ? 'No last statements found.'
                            : lastStatementOnly
                              ? 'No invoices found in the last statement for this account.'
                              : 'No outstanding invoices found matching the criteria.'
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <Row className="mt-3 align-items-start">
                <Col md={6}>
                  <Form.Group controlId="receivePaymentMemo">
                    <Form.Label>Memo</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder=""
                      name="memo"
                      value={memo}
                      onChange={handleInputChange}
                      disabled={!userLabId || backendError}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex flex-column align-items-end">
                  <div className="d-flex justify-content-between w-100 mb-2 pt-1 pb-1">
                    <span>Amount to Apply</span>
                    <span style={{ fontWeight: 'bold' }}>{(amountToApply)}</span>
                  </div>
                  <div className="d-flex justify-content-between w-100 align-items-center">
                    <span style={{ fontSize: '0.9em', color: '#6c757d', paddingRight: '10px' }}>
                      Payment amount exceeding the outstanding invoices total creates a new unapplied credit
                    </span>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {(unappliedCredit)}
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', paddingTop: 0 }}>
        <Button variant="light" onClick={handleCloseModal} style={{ minWidth: '80px' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          style={{
            ...styles.footerButton,
            ...((!userLabId || backendError) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
          }}
          onMouseOver={e => {
            if (userLabId && !backendError) {
              e.currentTarget.style.backgroundColor = styles.footerButtonHover.backgroundColor;
              e.currentTarget.style.borderColor = styles.footerButtonHover.borderColor;
            }
          }}
          onMouseOut={e => {
            if (userLabId && !backendError) {
              e.currentTarget.style.backgroundColor = styles.footerButton.backgroundColor;
              e.currentTarget.style.borderColor = styles.footerButton.borderColor;
            }
          }}
          disabled={!userLabId || backendError}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default Payment;